import { useState, useMemo } from "react";
import { ArrowLeft, Download, RotateCcw, Info } from "lucide-react";

const usd = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const usd2 = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Field = ({ label, value, setValue, min, max, step, suffix }) => (
  <div className="space-y-2" data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="flex items-baseline justify-between gap-3">
      <label className="font-sans text-sm text-ink-soft">{label}</label>
      <div className="flex items-baseline gap-1">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setValue(Number(e.target.value))}
          className="w-24 bg-transparent border-b border-ink/20 text-right font-serif text-2xl text-ink focus:outline-none focus:border-terracotta transition-colors tabular-nums"
        />
        {suffix && <span className="font-sans text-xs text-ink-soft">{suffix}</span>}
      </div>
    </div>
    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => setValue(Number(e.target.value))}
      className="w-full accent-terracotta cursor-pointer"
    />
  </div>
);

const Stat = ({ label, value, big, accent }) => (
  <div className={big ? "" : ""}>
    <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1">{label}</p>
    <p className={`font-serif leading-none ${big ? "text-5xl md:text-6xl" : "text-3xl"} ${accent ? "text-terracotta" : "text-ink"}`}>
      {value}
    </p>
  </div>
);

const Row = ({ label, value, strong }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="font-sans text-sm text-ink-soft">{label}</span>
    <span className={`font-serif tabular-nums ${strong ? "text-2xl text-ink" : "text-lg text-ink"}`}>{value}</span>
  </div>
);

export default function PLModel() {
  // Model A — nightly upsell
  const [covers, setCovers] = useState(150);
  const [nights, setNights] = useState(30);
  const [attach, setAttach] = useState(30);
  const [priceA, setPriceA] = useState(22);
  const [foodA, setFoodA] = useState(1.5);
  const [curd, setCurd] = useState("imported");
  const [laborNight, setLaborNight] = useState(120);

  // Model B — ticketed event
  const [eventsWk, setEventsWk] = useState(2);
  const [seats, setSeats] = useState(24);
  const [priceB, setPriceB] = useState(85);
  const [costB, setCostB] = useState(18);

  // Landed cost — curd supply chain
  const [curdFob, setCurdFob] = useState(5.5);
  const [freight, setFreight] = useState(3);
  const [handling, setHandling] = useState(0.8);
  const [bboPct, setBboPct] = useState(7);
  const [portionsKg, setPortionsKg] = useState(9);

  // Phase 2 — Pizzeria (Pinsa & Fuoco)
  const [pzPinsas, setPzPinsas] = useState(90);
  const [pzPrice, setPzPrice] = useState(19);
  const [pzFood, setPzFood] = useState(3.5);
  const [pzNights, setPzNights] = useState(30);

  const a = useMemo(() => {
    const plates = Math.round(covers * (attach / 100));
    const revNight = plates * priceA;
    const cogsNight = plates * foodA;
    const grossNight = revNight - cogsNight - laborNight;
    const grossMonth = grossNight * nights;
    const revMonth = revNight * nights;
    const foodPct = priceA > 0 ? (foodA / priceA) * 100 : 0;
    return { plates, revNight, grossNight, grossMonth, revMonth, foodPct };
  }, [covers, nights, attach, priceA, foodA, laborNight]);

  const b = useMemo(() => {
    const revWeek = eventsWk * seats * priceB;
    const cogsWeek = eventsWk * seats * costB;
    const grossWeek = revWeek - cogsWeek;
    const grossMonth = grossWeek * 4.3;
    const revMonth = revWeek * 4.3;
    return { revWeek, grossWeek, grossMonth, revMonth };
  }, [eventsWk, seats, priceB, costB]);

  const totalMonth = a.grossMonth + b.grossMonth;

  const landed = useMemo(() => {
    const cif = curdFob + freight + handling;
    const tax = cif * (bboPct / 100);
    const perKg = cif + tax;
    const perPortion = portionsKg > 0 ? perKg / portionsKg : 0;
    return { cif, tax, perKg, perPortion };
  }, [curdFob, freight, handling, bboPct, portionsKg]);

  const pizzeriaMonth = useMemo(
    () => pzPinsas * (pzPrice - pzFood) * pzNights,
    [pzPinsas, pzPrice, pzFood, pzNights]
  );
  const annualCombined = (totalMonth + pizzeriaMonth) * 12;

  const galleryImgs = [
    { src: "https://images.unsplash.com/photo-1782112415996-f6d5926f399f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Burrata on rich pomodoro" },
    { src: "https://images.unsplash.com/photo-1571081538808-612772b00e9f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Burrata & toasted bread" },
    { src: "https://images.unsplash.com/photo-1580638149300-65f0b9e8fbff?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Burrata, heirloom tomatoes" },
    { src: "https://images.unsplash.com/photo-1676300185089-8781af127fee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", alt: "Caprese, hand-pulled mozzarella" },
  ];

  const reset = () => {
    setCovers(150); setNights(30); setAttach(30); setPriceA(22); setFoodA(1.5); setLaborNight(120);
    setEventsWk(2); setSeats(24); setPriceB(85); setCostB(18);
    setCurdFob(5.5); setFreight(3); setHandling(0.8); setBboPct(7); setPortionsKg(9);
    setPzPinsas(90); setPzPrice(19); setPzFood(3.5); setPzNights(30);
  };

  return (
    <div className="min-h-screen bg-bg" data-testid="pl-model-page">
      <div className="noise-overlay" aria-hidden="true" />

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-bg/80 backdrop-blur-xl border-b border-ink/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" data-testid="back-home" className="inline-flex items-center gap-2 font-sans text-sm text-ink-soft hover:text-ink transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </a>
          <span className="font-serif text-2xl md:text-3xl tracking-tight text-ink">Filata<span className="text-terracotta">.</span></span>
          <a
            href="/pizzeria"
            data-testid="pl-pizzeria-link"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-sans text-sm text-bg hover:bg-terracotta transition-colors"
          >
            Pizzeria concept →
          </a>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="max-w-2xl mb-14">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-4">P&amp;L Model · For the Owner</p>
          <h1 className="font-serif text-5xl md:text-6xl text-ink leading-[0.95] tracking-tight">
            The Mozzarella Show, <em className="italic">by the numbers.</em>
          </h1>
          <p className="font-sans text-base text-ink-soft mt-6 leading-relaxed">
            Adjust the assumptions and watch the margins update live. Two ways to run it: a nightly
            upsell at the table, or ticketed chef's-table events. Combine both for the full picture.
          </p>
          <button onClick={reset} data-testid="reset-btn" className="inline-flex items-center gap-2 mt-6 font-sans text-sm text-ink-soft hover:text-terracotta transition-colors">
            <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
          </button>
        </div>

        <div
          data-testid="pl-nb-note"
          className="mb-12 md:mb-16 flex gap-4 rounded-sm border-l-4 border-terracotta bg-terracotta/10 p-5 md:p-6"
        >
          <Info className="h-5 w-5 md:h-6 md:w-6 text-terracotta shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-terracotta mb-1.5">N.B.</p>
            <p className="font-sans text-sm md:text-base text-ink leading-relaxed">
              The estimated revenues below refer <strong>only to the concept's specific products</strong>
              {" "}(fresh mozzarella / pinsa). They are to be considered <strong>additional turnover</strong>,
              on top of the restaurant's normal business volume — not a replacement for existing sales.
            </p>
          </div>
        </div>

        <div className="mb-12 md:mb-16" data-testid="pl-gallery">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">The product behind the numbers</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {galleryImgs.map((g, i) => (
              <figure key={i} className="group relative overflow-hidden rounded-sm aspect-[3/4]">
                <img
                  src={g.src}
                  alt={g.alt}
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/75 to-transparent p-3 pt-10">
                  <span className="font-serif italic text-bg text-base md:text-lg">{g.alt}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Model A */}
          <section className="bg-surface border border-ink/10 rounded-sm p-7 md:p-9" data-testid="model-a">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-serif text-3xl text-ink">Model A</h2>
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-ink-soft">Nightly upsell</span>
            </div>
            <div className="space-y-7">
              <div className="space-y-3" data-testid="curd-mode">
                <label className="font-sans text-sm text-ink-soft">Curd sourcing</label>
                <div className="flex gap-2">
                  {[
                    { k: "imported", label: "Imported curd", cost: 1.5 },
                    { k: "onsite", label: "On-site production", cost: 0.9 },
                  ].map((m) => (
                    <button
                      key={m.k}
                      data-testid={`curd-${m.k}`}
                      onClick={() => { setCurd(m.k); setFoodA(m.cost); }}
                      className={`flex-1 rounded-full px-3 py-2.5 text-xs font-sans tracking-wide border transition-colors ${
                        curd === m.k ? "bg-ink text-bg border-ink" : "border-ink/20 text-ink-soft hover:border-ink"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <p className="font-sans text-xs text-ink-soft leading-relaxed">
                  {curd === "imported"
                    ? "Reliable dual-supplier plan. Higher COGS, zero downtime — best to launch."
                    : "Lower COGS at scale, deeper authenticity — needs equipment, milk sourcing & dairy permits."}
                </p>
              </div>
              <Field label="Covers / night" value={covers} setValue={setCovers} min={40} max={400} step={5} />
              <Field label="Attach rate" value={attach} setValue={setAttach} min={5} max={80} step={1} suffix="%" />
              <Field label="Price / plate" value={priceA} setValue={setPriceA} min={12} max={45} step={1} suffix="$" />
              <Field label="Food cost / plate" value={foodA} setValue={setFoodA} min={0.5} max={6} step={0.5} suffix="$" />
              <Field label="Extra labor / night" value={laborNight} setValue={setLaborNight} min={0} max={500} step={10} suffix="$" />
              <Field label="Nights / month" value={nights} setValue={setNights} min={4} max={31} step={1} />
            </div>
            <div className="mt-9 pt-7 border-t border-ink/10 grid grid-cols-2 gap-6">
              <Stat label="Plates / night" value={a.plates} />
              <Stat label="Food cost" value={`${a.foodPct.toFixed(1)}%`} />
              <Stat label="Revenue / month" value={usd(a.revMonth)} />
              <Stat label="Gross margin / month" value={usd(a.grossMonth)} accent big />
            </div>
          </section>

          {/* Model B */}
          <section className="bg-surface border border-ink/10 rounded-sm p-7 md:p-9" data-testid="model-b">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="font-serif text-3xl text-ink">Model B</h2>
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-ink-soft">Ticketed events</span>
            </div>
            <div className="space-y-7">
              <Field label="Events / week" value={eventsWk} setValue={setEventsWk} min={0} max={7} step={1} />
              <Field label="Seats / event" value={seats} setValue={setSeats} min={8} max={60} step={2} />
              <Field label="Ticket price" value={priceB} setValue={setPriceB} min={45} max={195} step={5} suffix="$" />
              <Field label="Cost / guest" value={costB} setValue={setCostB} min={5} max={60} step={1} suffix="$" />
            </div>
            <div className="mt-9 pt-7 border-t border-ink/10 grid grid-cols-2 gap-6">
              <Stat label="Revenue / week" value={usd(b.revWeek)} />
              <Stat label="Revenue / month" value={usd(b.revMonth)} />
              <Stat label="Gross / week" value={usd(b.grossWeek)} />
              <Stat label="Gross margin / month" value={usd(b.grossMonth)} accent big />
            </div>
          </section>
        </div>

        {/* Landed cost — curd supply chain */}
        <section className="bg-surface border border-ink/10 rounded-sm mt-8 lg:mt-10 p-7 md:p-9" data-testid="landed-cost">
          <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
            <h2 className="font-serif text-3xl text-ink">Landed cost — curd supply chain</h2>
            <span className="font-sans text-xs uppercase tracking-[0.2em] text-ink-soft">Curd → freight → customs → plate</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="space-y-7">
              <Field label="Curd FOB (US)" value={curdFob} setValue={setCurdFob} min={3} max={14} step={0.1} suffix="$/kg" />
              <Field label="Refrigerated freight" value={freight} setValue={setFreight} min={0} max={6} step={0.1} suffix="$/kg" />
              <Field label="Handling / broker" value={handling} setValue={setHandling} min={0} max={3} step={0.1} suffix="$/kg" />
              <Field label="BBO at import" value={bboPct} setValue={setBboPct} min={0} max={15} step={0.5} suffix="%" />
              <Field label="Portions / kg" value={portionsKg} setValue={setPortionsKg} min={6} max={12} step={1} />
            </div>
            <div className="flex flex-col justify-between">
              <div className="space-y-4">
                <Row label="CIF (curd + freight + handling)" value={`${usd2(landed.cif)} /kg`} />
                <Row label={`Duty (HS 0406.90 ≈ 0%) + BBO ${bboPct}%`} value={`+ ${usd2(landed.tax)} /kg`} />
                <div className="h-px bg-ink/10" />
                <Row label="Landed cost" value={`${usd2(landed.perKg)} /kg`} strong />
              </div>
              <div className="mt-8 bg-wine text-bg rounded-sm p-6">
                <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-bg/50 mb-1">Cost per portion</p>
                <p className="font-serif text-5xl text-terracotta leading-none" data-testid="landed-per-portion">{usd2(landed.perPortion)}</p>
                <button
                  onClick={() => setFoodA(Number(landed.perPortion.toFixed(2)))}
                  data-testid="apply-to-model-a"
                  className="inline-flex items-center gap-2 mt-4 rounded-full bg-bg px-5 py-2.5 font-sans text-sm text-ink hover:bg-terracotta hover:text-white transition-colors"
                >
                  Use in Model A →
                </button>
              </div>
            </div>
          </div>
          <p className="font-sans text-xs text-ink-soft mt-6 italic">
            Estimates from public sources — validate with supplier quotes and a local customs broker. Riding an existing cold-chain to the venue can make incremental freight far lower.
          </p>
        </section>

        {/* Combined */}
        <section className="bg-wine text-bg rounded-sm mt-8 lg:mt-10 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="combined-total">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.35em] text-bg/50 mb-3">Combined estimate</p>
            <h2 className="font-serif text-4xl md:text-5xl">Total gross margin / month</h2>
            <p className="font-sans text-sm text-bg/60 mt-3 max-w-lg">
              Model A + Model B, before rent/overhead allocation. Estimates for discussion — validate
              with the venue's real covers and curd pricing.
            </p>
          </div>
          <p className="font-serif text-6xl md:text-7xl text-terracotta leading-none whitespace-nowrap" data-testid="total-value">
            {usd(totalMonth)}
          </p>
        </section>

        <section className="bg-surface border border-ink/10 rounded-sm mt-8 lg:mt-10 p-7 md:p-10" data-testid="annual-outlook">
          <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
            <h2 className="font-serif text-3xl text-ink">Phase 1 + Phase 2 — Annual outlook</h2>
            <span className="font-sans text-xs uppercase tracking-[0.2em] text-ink-soft">Mozzarella Show + Pinseria</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="space-y-7">
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-terracotta">Phase 2 · Pinseria assumptions</p>
              <Field label="Pinsas / night" value={pzPinsas} setValue={setPzPinsas} min={20} max={300} step={5} />
              <Field label="Price / pinsa" value={pzPrice} setValue={setPzPrice} min={10} max={40} step={1} suffix="$" />
              <Field label="Food cost / pinsa" value={pzFood} setValue={setPzFood} min={1} max={10} step={0.5} suffix="$" />
              <Field label="Nights / month" value={pzNights} setValue={setPzNights} min={10} max={31} step={1} />
            </div>
            <div className="flex flex-col justify-center gap-4">
              <Row label="Mozzarella Show — gross / month" value={usd(totalMonth)} />
              <Row label="Pinseria — gross / month" value={usd(pizzeriaMonth)} />
              <div className="h-px bg-ink/10" />
              <Row label="Combined — gross / month" value={usd(totalMonth + pizzeriaMonth)} strong />
              <div className="mt-4 bg-wine text-bg rounded-sm p-6">
                <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-bg/50 mb-1">Combined gross margin / year</p>
                <p className="font-serif text-5xl md:text-6xl text-terracotta leading-none" data-testid="annual-value">{usd(annualCombined)}</p>
              </div>
            </div>
          </div>
          <p className="font-sans text-xs text-ink-soft mt-6 italic">
            Combined estimate before rent/overhead. Illustrative only — validate with real covers, pricing and ingredient costs.
          </p>
        </section>

        <p className="font-sans text-xs text-ink-soft mt-8 italic">
          Disclaimer: all figures are user-driven estimates for planning discussion only, not a financial commitment.
        </p>
      </main>
    </div>
  );
}
